import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, Plus, Check, Play, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { VideoModal, LoginPromptModal, SubscribePlanModal } from './NetflixLandingPage';
import { pickSource, clipLabel } from '../lib/media';
import { useSaved } from '../lib/useSaved';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#c8a672';
const BONE = '#f5efe6';
const PAGE = 48;

const TABS = [
  { key: 'all', label: 'All Videos' },
  { key: 'saved', label: 'My List' },
  { key: 'purchased', label: 'Purchased' },
];
const TYPES = [
  { key: 'all', label: 'Everything' },
  { key: 'library', label: 'Library' },
  { key: 'event-video', label: 'Event clips' },
  { key: 'past', label: 'Replays' },
  { key: 'studio-project', label: 'Studio' },
];

const isTest = (r) => /^test[_ ]|^TEST_/i.test(r?.title || '');

const toItem = (kind, r) => {
  if (kind === 'event-video') {
    return { kind, id: r.id, title: clipLabel(r), sub: 'Event clip', thumb: r.thumbnail_url, eventId: r.event_id, eventTitle: r.event_title, date: r.uploaded_at || r.published_at, record: r };
  }
  if (kind === 'past') {
    return { kind, id: r.id, title: r.title, sub: r.event_date || 'Replay', thumb: r.thumbnail_url, date: r.event_date, record: r };
  }
  if (kind === 'studio-project') {
    return { kind, id: r.id, title: r.title || 'Studio project', sub: r.coach_name ? `by ${r.coach_name}` : 'Video Studio', thumb: r.thumbnail_url, date: r.created_at, record: { ...r, embed_code: r.final_video_url } };
  }
  return { kind, id: r.id, title: r.title, sub: [r.category, r.difficulty].filter(Boolean).join(' · ') || 'Library', thumb: r.thumbnail_url, date: r.created_at, record: r };
};

function useBrowseData(authed) {
  const [state, setState] = useState({ loading: true, items: [], purchases: [] });
  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const get = (url, h = {}) => axios.get(`${API_URL}${url}`, { headers: h, timeout: 15000 }).then((r) => r.data).catch(() => null);
    Promise.all([
      authed ? get('/api/library', headers) : get('/api/preview/library'),
      get('/api/event-videos?per_event=2000'),
      get('/api/past-events'),
      get('/api/public/video-projects'),
      authed ? get('/api/me/purchases', headers) : Promise.resolve(null),
    ]).then(([lib, ev, past, studio, buys]) => {
      const clips = (ev?.events || []).flatMap((g) => g.videos || []);
      const items = [
        ...(lib?.videos || []).filter((v) => !isTest(v)).map((v) => toItem('library', v)),
        ...clips.map((v) => toItem('event-video', v)),
        ...(past?.events || []).filter((e) => !isTest(e)).map((e) => toItem('past', e)),
        ...(studio?.projects || []).map((p) => toItem('studio-project', p)),
      ].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
      setState({ loading: false, items, purchases: (buys?.events || []).map((e) => toItem('past', e)) });
    });
  }, [authed]);
  return state;
}

const VideoCard = ({ item, onPlay, saved, onToggleSave }) => (
  <div className="group relative rounded-lg overflow-hidden bg-[#1a1410] border border-white/10 hover:border-[#c8a672]/60 transition-colors" data-testid={`browse-card-${item.kind}-${item.id}`}>
    <button type="button" onClick={onPlay} className="block w-full text-left" data-testid={`browse-play-${item.kind}-${item.id}`}>
      <div className="aspect-video bg-black relative">
        {item.thumb ? (
          <img src={item.thumb} alt={item.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 text-xs uppercase tracking-widest">Elevated</div>
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/30 transition-opacity">
          <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: BONE, color: '#0a0705' }}>
            <Play className="w-5 h-5 fill-current" />
          </span>
        </div>
      </div>
      <div className="p-3 pr-12">
        <div className="text-sm font-semibold text-white line-clamp-2 leading-snug">{item.title}</div>
        <div className="text-[11px] uppercase tracking-widest text-white/50 mt-1 truncate">{item.sub}</div>
      </div>
    </button>
    {onToggleSave && (
      <button
        type="button"
        onClick={onToggleSave}
        className="absolute bottom-3 right-3 w-8 h-8 rounded-full flex items-center justify-center border transition-colors"
        style={saved ? { background: GOLD, color: '#0a0705', borderColor: GOLD } : { color: BONE, borderColor: 'rgba(255,255,255,0.3)' }}
        aria-label={saved ? 'Remove from My List' : 'Add to My List'}
        data-testid={`browse-save-${item.kind}-${item.id}`}
      >
        {saved ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
      </button>
    )}
  </div>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-3.5 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap transition-colors"
    style={active ? { background: BONE, color: '#0a0705', borderColor: BONE } : { color: BONE, borderColor: 'rgba(255,255,255,0.2)' }}
    data-testid={testid}
  >
    {children}
  </button>
);

const EmptyState = ({ tab, authed }) => (
  <div className="py-24 text-center text-white/60" data-testid="browse-empty">
    {!authed && tab !== 'all' ? (
      <>Sign in to see your {tab === 'saved' ? 'saved videos' : 'purchases'}. <Link to="/login" className="underline" style={{ color: GOLD }}>Sign in</Link></>
    ) : tab === 'saved' ? 'Nothing saved yet — tap the + on any video to add it to My List.'
      : tab === 'purchased' ? 'No purchased replays yet.'
        : 'No videos match your search.'}
  </div>
);

function BrowseContent() {
  const { user } = useAuth();
  const authed = typeof window !== 'undefined' && !!localStorage.getItem('token');
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'all';
  const [type, setType] = useState('all');
  const [eventId, setEventId] = useState('all');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [playing, setPlaying] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [subOpen, setSubOpen] = useState(false);
  const { loading, items, purchases } = useBrowseData(authed);
  const saved = useSaved(!!user);

  const canWatch = !!user && (user.role === 'admin' || user.role === 'coach' || ['active', 'trial'].includes(user.subscription_status));

  const events = useMemo(() => {
    const m = new Map();
    for (const it of items) if (it.kind === 'event-video' && it.eventId && it.eventTitle) m.set(it.eventId, it.eventTitle);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const list = useMemo(() => {
    let base = items;
    if (tab === 'saved') {
      const byKey = new Map([...items, ...purchases].map((it) => [`${it.kind}:${it.id}`, it]));
      base = saved.items.map((s) => byKey.get(`${s.kind}:${s.id}`)).filter(Boolean);
    } else if (tab === 'purchased') {
      base = purchases;
    }
    if (type !== 'all') base = base.filter((it) => it.kind === type);
    if (eventId !== 'all') base = base.filter((it) => it.eventId === eventId);
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length) {
      base = base.filter((it) => {
        const hay = `${it.title} ${it.sub} ${it.eventTitle || ''}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      });
    }
    return base;
  }, [items, purchases, saved.items, tab, type, eventId, q]);

  useEffect(() => { setLimit(PAGE); }, [tab, type, eventId, q]);

  const play = (it) => {
    if (!authed || !user) return setPrompt({ title: it.title, mode: 'login' });
    const owned = it.kind === 'past' && purchases.some((p) => p.id === it.id);
    if (!canWatch && !owned) return setPrompt({ title: it.title, mode: 'subscribe' });
    setPlaying({ ...it, src: pickSource(it.record) });
  };

  return (
    <div className="min-h-screen bg-[#0a0705] text-white" data-testid="browse-page">
      <header className="sticky top-0 z-30 border-b border-white/10 backdrop-blur-xl" style={{ background: 'rgba(10,7,5,0.85)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center gap-3">
          <Link to="/" className="p-2 -ml-2 rounded-md hover:bg-white/10" aria-label="Back to home" data-testid="browse-back">
            <ArrowLeft className="w-5 h-5" style={{ color: BONE }} />
          </Link>
          <h1 className="font-display uppercase tracking-wide text-xl sm:text-2xl" style={{ color: BONE }}>Library</h1>
          <div className="ml-auto flex items-center gap-2 border border-white/20 bg-black/50 rounded-md px-3 py-2 w-full max-w-[320px]">
            <Search className="w-4 h-4 text-white/60 shrink-0" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search videos, events…"
              className="bg-transparent outline-none text-sm w-full placeholder-white/40"
              data-testid="browse-search"
            />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-3 flex gap-6 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setParams(t.key === 'all' ? {} : { tab: t.key })}
              className="pb-2 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors"
              style={tab === t.key ? { color: BONE, borderColor: GOLD } : { color: 'rgba(255,255,255,0.55)', borderColor: 'transparent' }}
              data-testid={`browse-tab-${t.key}`}
            >
              {t.label}
              {t.key === 'saved' && saved.items.length > 0 && <span className="ml-1.5 text-xs" style={{ color: GOLD }}>{saved.items.length}</span>}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {TYPES.map((t) => (
            <Chip key={t.key} active={type === t.key} onClick={() => setType(t.key)} testid={`browse-type-${t.key}`}>{t.label}</Chip>
          ))}
          {events.length > 0 && (
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="ml-auto h-9 rounded-full border border-white/20 bg-black/60 px-3 text-xs text-white max-w-[260px]"
              data-testid="browse-event-filter"
            >
              <option value="all">All events</option>
              {events.map(([id, title]) => <option key={id} value={id}>{title}</option>)}
            </select>
          )}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center" data-testid="browse-loading"><Loader2 className="w-6 h-6 animate-spin" style={{ color: GOLD }} /></div>
        ) : list.length === 0 ? (
          <EmptyState tab={tab} authed={authed} />
        ) : (
          <>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-3" data-testid="browse-count">{list.length} video{list.length === 1 ? '' : 's'}</div>
            <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="browse-grid">
              {list.slice(0, limit).map((it) => (
                <VideoCard
                  key={`${it.kind}-${it.id}`}
                  item={it}
                  onPlay={() => play(it)}
                  saved={saved.isSaved(it.kind, it.id)}
                  onToggleSave={user ? () => saved.toggle(it.kind, it.id) : null}
                />
              ))}
            </div>
            {list.length > limit && (
              <div className="flex justify-center mt-8">
                <button type="button" onClick={() => setLimit((n) => n + PAGE)} className="px-6 py-2.5 rounded-full text-sm font-semibold border border-white/25 hover:bg-white/10 transition-colors" style={{ color: BONE }} data-testid="browse-load-more">
                  Show more ({list.length - limit} left)
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <VideoModal
        open={!!playing}
        onClose={() => setPlaying(null)}
        title={playing?.title}
        embedCode={playing?.src}
        videoId={playing?.id}
        videoKind={playing && ['library', 'event-video'].includes(playing.kind) ? playing.kind : null}
        saved={!!playing && saved.isSaved(playing.kind, playing.id)}
        onToggleSave={playing && user ? () => saved.toggle(playing.kind, playing.id) : null}
      />
      <LoginPromptModal open={!!prompt} onClose={() => setPrompt(null)} title={prompt?.title} mode={prompt?.mode || 'login'} onSubscribe={() => setSubOpen(true)} />
      <SubscribePlanModal open={subOpen} onClose={() => setSubOpen(false)} isAuthenticated={authed && !!user} />
    </div>
  );
}

// Library is members-only: signed-out visitors go to sign in, then come back here.
export default function BrowsePage() {
  const location = useLocation();
  if (!localStorage.getItem('token')) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return <BrowseContent />;
}
